import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Users, 
  Mic, 
  FileText, 
  Activity,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Heart,
  Scale,
  HeartHandshake
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeTenants: 0,
    activeSessions: 0,
    totalIntakes: 0,
    agentStatus: 'online'
  });
  const [tenants, setTenants] = useState([]);
  const [recentIntakes, setRecentIntakes] = useState([]);
  const [error, setError] = useState(null);

  const industryIcons = {
    Medical: Heart,
    Legal: Scale,
    AgedCare: HeartHandshake
  };

  const industryColors = {
    Medical: '#EF4444',
    Legal: '#3B82F6',
    AgedCare: '#10B981'
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [tenantsRes, intakesRes, healthRes, agentStatusRes] = await Promise.allSettled([
          axios.get(`${API}/tenants`),
          axios.get(`${API}/intakes`),
          axios.get(`${API}/health`),
          axios.get(`${API}/agents/status`)
        ]);

        // Process tenants data
        if (tenantsRes.status === 'fulfilled') {
          const tenantsData = tenantsRes.value.data;
          setTenants(tenantsData || []);
          setStats(prev => ({ ...prev, activeTenants: tenantsData?.length || 0 }));
        }

        // Process intakes data
        if (intakesRes.status === 'fulfilled') {
          const intakesData = intakesRes.value.data;
          setRecentIntakes(intakesData?.slice(0, 5) || []);
          setStats(prev => ({ 
            ...prev, 
            totalIntakes: intakesData?.length || 0,
            activeSessions: intakesData?.filter(i => i.status === 'active')?.length || 0
          }));
        }

        // Process agent status
        if (agentStatusRes.status === 'fulfilled') {
          const agentData = agentStatusRes.value.data;
          setStats(prev => ({ ...prev, agentStatus: agentData?.status || 'offline' }));
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Prepare chart data
  const tenantDistribution = tenants.reduce((acc, tenant) => {
    const industry = tenant.industry_type || 'Unknown';
    const existing = acc.find(item => item.name === industry);
    if (existing) {
      existing.value++;
    } else {
      acc.push({ 
        name: industry, 
        value: 1, 
        color: industryColors[industry] || '#6B7280'
      });
    }
    return acc;
  }, []);

  const chartColors = ['#00D4AA', '#0EA5E9', '#EF4444', '#10B981', '#F59E0B'];

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">Error Loading Dashboard</h3>
          <p className="text-slate-400">{error}</p>
          <Button 
            onClick={() => window.location.reload()} 
            className="mt-4"
            data-testid="retry-button"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-300">Active Tenants</CardTitle>
            <Users className="h-4 w-4 text-teal-400" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16 bg-slate-700" />
            ) : (
              <>
                <div className="text-2xl font-bold text-white" data-testid="active-tenants-count">
                  {stats.activeTenants}
                </div>
                <p className="text-xs text-slate-400">
                  <TrendingUp className="inline h-3 w-3 mr-1" />
                  +2 from last month
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-300">Active Sessions</CardTitle>
            <Mic className="h-4 w-4 text-blue-400" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16 bg-slate-700" />
            ) : (
              <>
                <div className="text-2xl font-bold text-white" data-testid="active-sessions-count">
                  {stats.activeSessions}
                </div>
                <p className="text-xs text-slate-400">
                  <Activity className="inline h-3 w-3 mr-1" />
                  Real-time conversations
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-300">Total Intakes</CardTitle>
            <FileText className="h-4 w-4 text-green-400" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16 bg-slate-700" />
            ) : (
              <>
                <div className="text-2xl font-bold text-white" data-testid="total-intakes-count">
                  {stats.totalIntakes}
                </div>
                <p className="text-xs text-slate-400">
                  <TrendingUp className="inline h-3 w-3 mr-1" />
                  +15% from last week
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-300">Agent Status</CardTitle>
            <Activity className="h-4 w-4 text-teal-400" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20 bg-slate-700" />
            ) : (
              <>
                <div className="flex items-center space-x-2" data-testid="agent-status">
                  <div className={`w-2 h-2 rounded-full ${
                    stats.agentStatus === 'online' ? 'bg-green-400 animate-pulse' : 'bg-red-400'
                  }`} />
                  <span className="text-2xl font-bold text-white capitalize">
                    {stats.agentStatus}
                  </span>
                </div>
                <p className="text-xs text-slate-400">
                  <CheckCircle className="inline h-3 w-3 mr-1" />
                  All systems operational
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tenant Distribution Chart */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Tenant Distribution by Industry</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full bg-slate-700" />
            ) : tenantDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={tenantDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {tenantDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || chartColors[index % chartColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip className="bg-slate-700 border-slate-600" />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-slate-400">
                No tenant data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Intake Sessions */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Recent Intake Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center space-x-4">
                    <Skeleton className="h-10 w-10 rounded-full bg-slate-700" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-full bg-slate-700" />
                      <Skeleton className="h-3 w-2/3 bg-slate-700" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentIntakes.length > 0 ? (
              <div className="space-y-4" data-testid="recent-intakes-list">
                {recentIntakes.map((intake) => {
                  const IndustryIcon = industryIcons[intake.tenant?.industry] || FileText;
                  const industryColor = industryColors[intake.tenant?.industry] || '#6B7280';
                  
                  return (
                    <div key={intake.id} className="flex items-center space-x-4 p-3 rounded-lg bg-slate-700/50 hover:bg-slate-700/80 transition-colors">
                      <div 
                        className="flex items-center justify-center w-10 h-10 rounded-full"
                        style={{ backgroundColor: `${industryColor}20` }}
                      >
                        <IndustryIcon className="w-5 h-5" style={{ color: industryColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-white truncate">
                            {intake.tenant?.name || 'Unknown Tenant'}
                          </p>
                          <Badge 
                            variant={intake.status === 'active' ? 'default' : 'secondary'}
                            className={intake.status === 'active' ? 'bg-green-500/20 text-green-400' : ''}
                          >
                            {intake.status || 'completed'}
                          </Badge>
                        </div>
                        <div className="flex items-center text-xs text-slate-400">
                          <Clock className="w-3 h-3 mr-1" />
                          {intake.created_at ? new Date(intake.created_at).toLocaleString() : 'Just now'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-slate-400">
                <div className="text-center">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No recent intake sessions</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;